/**
 * HelpButton — opens the support modal.
 *
 * Two modes:
 *   variant="floating" (default) — fixed bottom-right floating action button
 *     (Intercom/Drift-style). Rendered globally in App.tsx so it appears on
 *     every authenticated page without overlapping any header band.
 *   variant="inline" — renders as a normal button in flow; used inside the
 *     Pilot header next to Sign out where an inline placement is preferred.
 *
 * The floating button hides itself on /pilot routes — the pilot header
 * renders its own inline HelpButton.
 *
 * Renders only when a session exists.
 *
 * Sub28 (2026-05-14) — three polishes:
 *   1. Global keyboard shortcut Cmd+Shift+H (Mac) / Ctrl+Shift+H (Win/Linux)
 *      opens the modal from any route.
 *   2. Floating button positioning respects iOS / Android safe-area insets
 *      so it never hides behind the mobile nav bar in standalone PWA mode.
 *   3. aria-keyshortcuts hint is now announced by screen readers.
 */
import { useState, useEffect } from "react";
import { getSession } from "@/lib/session";
import { HelpModal } from "./HelpModal";

interface Props {
  variant?: "floating" | "inline";
}

export default function HelpButton({ variant = "floating" }: Props) {
  const [open, setOpen] = useState(false);
  const [hash, setHash] = useState<string>(() =>
    typeof window !== "undefined" ? window.location.hash : "",
  );
  const session = getSession();

  useEffect(() => {
    if (variant !== "floating") return;
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [variant]);

  // Global keyboard shortcut: Cmd+Shift+H / Ctrl+Shift+H. Registered only
  // on the floating variant so the inline pilot-header copy does not double
  // up the listener.
  useEffect(() => {
    if (variant !== "floating") return;
    function handleKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && e.key.toLowerCase() === "h") {
        // Skip if the user is typing inside a contenteditable or input —
        // the platform shortcut for "highlight" should still work in those
        // contexts. The modal can still be opened via the button.
        const target = e.target as HTMLElement | null;
        const inEditable =
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable);
        if (inEditable) return;
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [variant]);

  if (!session) return null;

  // Hide the floating Help on /pilot routes — the pilot header renders an
  // inline Help button there to avoid overlapping Sign out.
  if (variant === "floating" && hash.startsWith("#/pilot")) return null;

  const floatingStyle: React.CSSProperties =
    variant === "floating"
      ? {
          // Respect iOS / Android safe-area insets so the FAB does not hide
          // behind the home indicator or mobile nav bar in standalone PWA.
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)",
          right: "calc(env(safe-area-inset-right, 0px) + 1.5rem)",
        }
      : {};

  const className =
    variant === "floating"
      ? "fixed z-40 whitespace-nowrap rounded-full bg-neutral-900 text-white px-4 py-2.5 shadow-lg hover:bg-neutral-800 text-[12px] font-medium transition-colors"
      : "text-[12px] px-3 py-2 rounded border border-border hover:bg-[hsl(var(--surface-2))] transition-colors";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={className}
        style={floatingStyle}
        data-testid="help-button"
        aria-label="Get help (Cmd+Shift+H)"
        aria-keyshortcuts="Meta+Shift+H Control+Shift+H"
        title="Get help — Cmd+Shift+H"
      >
        Help
      </button>
      <HelpModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
